import { HttpErrorResponse } from '@angular/common/http';
import { ContentChildren, Directive, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, QueryList } from '@angular/core';
import {
    AnonymousCredential,
    BlobUploadCommonResponse,
    BlockBlobClient,
    BlockBlobParallelUploadOptions,
    Pipeline,
    StoragePipelineOptions,
    newPipeline,
} from '@azure/storage-blob';
import {
    BlobParameter,
    BlobType,
    DatasetFileResponse,
    DatasetFileTypeOption,
    ErrorNotificationMode,
    FileModel,
    FileStorageType,
    Folder,
    UploadFileItem,
    UploadInfo,
    ValidAndInvalidFiles,
} from '@ca/core/models';
import { FileService, FileUploadService, FilesValidatorService, MachineLearningService, UserNotificationService } from '@ca/core/services';
import { environment } from '@ca/environment/environment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { FileItem, FileUploader, FileUploaderOptions } from 'ng2-file-upload';
import { EMPTY, Observable, Subject, from } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CreateFolder } from 'src/app/core/models/create-folder.model';
import * as FileUtils from '../../../../utils/file/file-utils';
import { UPLOADER_KEY } from '../../constants/constants';

@UntilDestroy()
@Directive({ selector: '[caApeerFileUpload]' })
export class ApeerFileUploadDirective implements OnInit {
    @Input() maxInputSizeInBytes = environment.defaultMaxFileSizeInBytes;
    @Input() showGeneralMaxInputSizeMessage = true;
    @Input() supportedFileTypes: string[] | undefined;
    @Input() notSupportedFileTypeCustomMessage?: string;
    @Input() uploadValidationSelectedFiles: FileModel[] = [];

    // Provide a unique key in order to allow saving the state ("cache")
    @Input() uploaderKey: string;

    // Pass URL from outside if it needs to be different than 'documents/upload/uploadinfo'.
    // If null, default is used.
    @Input() blobType: BlobType = BlobType.UserFile;

    /**
     * Name of the file.
     * Only used for BlobType.WorkflowImage, BlobType.ModuleImage, BlobType.EmbeddedImages, BlobType.TestFile and BlobType.UserVerificationDocument
     */
    @Input() pathToAppend?: string;
    /**
     * A base 64 string of an image which will automatically be uploaded to our blob storage. Currently used for pasting images
     * into Quill-Editor.
     */
    @Input() base64StringToUpload?: string;

    /**
     * If provided, will upload the file and connect it to given dataset
     */
    @Input() datasetId?: string;
    @Input() datasetFileType?: DatasetFileTypeOption;

    @Input() set errorNotificationMode(errorNotificationMode: ErrorNotificationMode) {
        this._errorNotificationMode = errorNotificationMode;
    }
    get errorNotificationMode(): ErrorNotificationMode {
        return this.isUploadToDataset ? 'confirmation' : this._errorNotificationMode;
    }

    /**
     * If provided, will upload the file to the target folder, otherwise to the root folder.
     */
    @Input() targetFolderId?: string;
    @Input() createTargetFolder?: CreateFolder;
    @Input() allowSameFileNames = true;
    @Input() allowDragAndDropMove = true;
    @Input() allowDragAndDropUpload = true;

    @Output() fileOver = new EventEmitter();
    @Output() fileDrop = new EventEmitter<File[]>();

    @Output() onReset: EventEmitter<void> = new EventEmitter<void>();
    @Output() onFilesAdded: EventEmitter<UploadFileItem[]> = new EventEmitter<UploadFileItem[]>();
    @Output() onUploadStarted: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onUploadCompleted: EventEmitter<FileModel> = new EventEmitter<FileModel>();
    @Output() onUploadCancelled: EventEmitter<void> = new EventEmitter<void>();
    @Output() onAllUploadsCompleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() onFileCreated: EventEmitter<FileModel> = new EventEmitter<FileModel>();
    @Output() onUploadRemoved: EventEmitter<UploadFileItem> = new EventEmitter<UploadFileItem>();
    @Output() targetFolderCreated = new EventEmitter<Folder>();
    @Output() invalidFiles = new EventEmitter<UploadFileItem[]>();

    @ContentChildren('trRef') trRef: QueryList<ElementRef<HTMLElement>>;

    // false: show individual error messages and emit error events
    // true: show confirmation dialog (ignore invalid files, but enable uploading valid files)
    get isUploadToDataset(): boolean {
        return this.datasetId != null;
    }

    protected element: ElementRef;

    private uploader: FileUploader;
    private pipeline: Pipeline;
    private readonly concurrency: number = 20;
    private readonly fileStorageType: FileStorageType = 'Azure';
    private readonly fileTypePrefix: string = 'file_';
    private readonly folderTypePrefix: string = 'folder_';
    private cancelUploadMap = new Map<FileItem, Subject<boolean>>();
    private cancelAllUploads$$ = new Subject<boolean>();
    private abortControllerMap = new Map<FileItem, { fileId: string; abortController: AbortController }>();
    private _errorNotificationMode: ErrorNotificationMode;

    constructor(
        element: ElementRef,
        private fileService: FileService,
        private fileUploadService: FileUploadService,
        private filesValidatorService: FilesValidatorService,
        private machineLearningService: MachineLearningService,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
    ) {
        this.element = element;
    }

    @Input() set cancelUpload(value: FileItem) {
        if (value) {
            setTimeout(() => {
                this.abortControllerMap.get(value)?.abortController.abort();
                this.cancelUploadMap.get(value)?.next(true);
                this.uploader.removeFromQueue(value);
                if (this.uploader.queue.length === 0) {
                    this.uploader.isUploading = false;
                }
                const fileId = this.abortControllerMap.get(value)?.fileId;
                if (fileId) {
                    if (this.isUploadToDataset) {
                        this.machineLearningService.removeFilesFromDataset$(this.datasetId!, [fileId]).subscribe();
                    } else {
                        this.fileService.deleteFiles$([fileId]).subscribe();
                    }
                }
                this.onUploadCancelled.emit();
            });
        }
    }

    @Input() set cancelAllUploads(value: { cancel: boolean }) {
        if (value?.cancel) {
            setTimeout(() => {
                const fileIds: string[] = [];
                this.uploader.queue.forEach(f => {
                    this.abortControllerMap.get(f)?.abortController.abort();
                    const fileId = this.abortControllerMap.get(f)?.fileId;
                    if (fileId) {
                        fileIds.push(fileId);
                    }
                });
                if (fileIds.length > 0) {
                    if (this.isUploadToDataset) {
                        this.machineLearningService.removeFilesFromDataset$(this.datasetId!, fileIds).subscribe();
                    } else {
                        this.fileService.deleteFiles$(fileIds).subscribe();
                    }
                }
                this.cancelAllUploads$$.next(true);
                this.uploader.clearQueue();
                this.uploader.isUploading = false;
            });
        }
    }

    ngOnInit(): void {
        this.createPipeline();
        this.initUploader();
    }

    getOptions(targetFolderId?: string): FileUploaderOptions | void {
        let options: FileUploaderOptions | void;
        if (this.uploader) {
            options = { ...this.uploader.options };
            if (targetFolderId) {
                if (!options.additionalParameter) {
                    options.additionalParameter = {};
                }
                options.additionalParameter.targetFolderId = targetFolderId;
            }
            return options;
        }
    }

    getFilters(): string {
        return '';
    }

    @HostListener('drop', ['$event']) onDrop(event: DragEvent): void {
        const haveFiles = this._haveFiles(
            this._getTransfer(event as DragEvent & { originalEvent: DragEvent })?.types as string[] & { contains: (item: string) => boolean },
        );
        if (haveFiles) {
            if (this.allowDragAndDropUpload) {
                this.uploadFiles(event);
            }
        } else {
            if (this.allowDragAndDropMove) {
                this.moveItems(event);
            }
        }
    }

    @HostListener('dragover', ['$event']) onDragOver(event: DragEvent): void {
        if (!this.uploader) {
            return;
        }
        const transfer = this._getTransfer(event as DragEvent & { originalEvent: DragEvent });
        const haveFiles = this._haveFiles(transfer?.types as string[] & { contains: (item: string) => boolean });

        if (haveFiles) {
            transfer!.dropEffect = 'copy';
            this._preventAndStop(event);
            this.fileOver.emit(true);
        } else {
            transfer!.dropEffect = 'move';
        }

        this.highlightDropTarget(<HTMLElement>event.target, !haveFiles, true);
    }

    @HostListener('dragleave', ['$event']) onDragLeave(event: DragEvent): void {
        if (!this.uploader) {
            return;
        }
        const targetElement: HTMLElement = <HTMLElement>event.target;
        const dropTarget: HTMLElement = this.getDropTarget(targetElement);
        if (!this.dragLeftTheElement(dropTarget, event)) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((this as any).element) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (event.currentTarget === (this as any).element[0]) {
                return;
            }
        }

        const haveFiles = this._haveFiles(
            this._getTransfer(event as DragEvent & { originalEvent: DragEvent })?.types as string[] & { contains: (item: string) => boolean },
        );

        this._preventAndStop(event);
        this.fileOver.emit(false);
        this.highlightDropTarget(targetElement, !haveFiles, false);
    }

    @HostListener('dragstart', ['$event']) onDragStart(event: DragEvent): void {
        // store dragged elements data
        const targetEl = this.getTargetRow(event.target as HTMLElement);

        if (!targetEl) {
            return;
        }

        let targetElId;
        let typePrefix: string;
        if (this.isTargetAFolder(targetEl)) {
            targetElId = this.getTargetFolderId(targetEl);
            typePrefix = this.folderTypePrefix;
        } else {
            targetElId = this.getRowId(targetEl);
            typePrefix = this.fileTypePrefix;
        }

        if (targetElId) {
            event.dataTransfer!.setData('text/plain', `${typePrefix}${targetElId}`);
        }
    }

    resetFileUploader(): void {
        this.onReset.emit();
    }

    protected _getTransfer(event: DragEvent & { originalEvent: DragEvent }): DataTransfer | null {
        return event.dataTransfer ? event.dataTransfer : event.originalEvent.dataTransfer; // jQuery fix;
    }

    protected _preventAndStop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }

    /**
     * When moving the files and folders returns false, when uploading files from the user system returns true.
     */
    protected _haveFiles(types: string[] & { contains: (item: string) => boolean }): boolean {
        if (!types) {
            return false;
        }

        if (types.indexOf) {
            return types.indexOf('Files') !== -1;
        } else if (types.contains) {
            return types.contains('Files');
        } else {
            return false;
        }
    }

    /**
     * Upload files from the user system.
     */
    private uploadFiles(event: DragEvent): void {
        if (!this.uploader || !event.target) {
            return;
        }
        const transfer = this._getTransfer(event as DragEvent & { originalEvent: DragEvent });
        if (!transfer || this.draggedItemsContainFolder(transfer.items)) {
            this.highlightDropTarget(<HTMLElement>event.target, false, false);
            return;
        }

        this._preventAndStop(event);
        const targetRow: HTMLTableRowElement | null = this.getTargetRow(<HTMLElement>event.target);
        const targetFolderId = targetRow ? this.getTargetFolderId(targetRow) : undefined;
        const options = this.getOptions(targetFolderId);
        const filters = this.getFilters();
        if (options) {
            this.uploader?.addToQueue(transfer.files as unknown as File[], options, filters);
        }
        this.fileOver.emit(false);
        this.fileDrop.emit(transfer.files as unknown as File[]);
        this.highlightDropTarget(<HTMLElement>event.target, false, false);
    }

    /**
     * Move files and folders to another folder.
     * Either single file, or selected files and/or folders.
     */
    private moveItems(event: DragEvent): void {
        if (!event.target) {
            return;
        }

        const targetRow = this.getTargetRow(<HTMLElement>event.target);
        if (!targetRow) {
            return;
        }

        const targetFolderId = this.getTargetFolderId(targetRow);
        const toElIsFolder = this.isTargetAFolder(targetRow);
        if (!targetFolderId || !toElIsFolder) {
            return;
        }

        const srcElId = event.dataTransfer!.getData('text/plain');

        const { fileIds, folderIds } = this.getSelectedFilesAndFolders(targetFolderId, srcElId);

        if (folderIds.includes(targetFolderId)) {
            return;
        }

        this.fileUploadService
            .move$(targetFolderId, fileIds, folderIds)
            .pipe(
                tap(() => {
                    this.onAllUploadsCompleted.emit();
                }),
            )
            .subscribe();
    }

    private draggedItemsContainFolder(items: DataTransferItemList): boolean {
        // rule is disabled because DataTransferItemList is not a regular list and it's not possible to use for of on it
        // eslint-disable-next-line
        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file' && items[i].webkitGetAsEntry()?.isDirectory) {
                return true;
            }
        }
        return false;
    }

    private getTargetRow(target: HTMLElement): HTMLTableRowElement | null {
        return target.closest('tr');
    }

    private getDropZoneElement(target?: HTMLElement): HTMLElement | null {
        let dropZoneElement: HTMLElement | null = null;
        if (target) {
            dropZoneElement = target.closest('.drop-zone');
            if (dropZoneElement == null) {
                target.getElementsByClassName('drop-zone');
            }
        }
        return dropZoneElement;
    }

    private getRowId(targetRow: HTMLTableRowElement): string | undefined {
        return targetRow?.getAttribute('data-rowId') || undefined;
    }

    private getTargetFolderId(targetRow: HTMLTableRowElement): string | undefined {
        return targetRow?.getAttribute('data-targetFolderId') || undefined;
    }

    private isTargetAFolder(targetRow: HTMLElement): boolean {
        return targetRow?.getAttribute('data-rowIsFolder') === 'true';
    }

    private getSelectedFilesAndFolders(targetFolderId: string, singleFileOrFolderIdWithTypePrefix: string): { fileIds: string[]; folderIds: string[] } {
        const isFolder = singleFileOrFolderIdWithTypePrefix.startsWith(this.folderTypePrefix);
        const singleFileOrFolderId = singleFileOrFolderIdWithTypePrefix.substring(singleFileOrFolderIdWithTypePrefix.indexOf('_') + 1);

        const fileIds: string[] = [];
        const folderIds: string[] = [];

        // Get all .custom-control-input elements
        const allCheckboxes = Array.from(document.querySelectorAll('.form-check-input')) as Array<HTMLInputElement>;

        // 1. Check if anything is checked.
        const isAnyChecked = allCheckboxes.some(checkbox => checkbox.checked);

        if (isAnyChecked) {
            // 2. Collect all checked items
            allCheckboxes.forEach(checkbox => {
                if (checkbox.checked) {
                    const trEl = this.getTargetRow(checkbox);
                    if (!trEl) {
                        return;
                    }

                    // If it's a folder
                    if (this.isTargetAFolder(trEl)) {
                        const folderId = this.getTargetFolderId(trEl);
                        // Make sure not to include the target folder itself
                        if (folderId && folderId !== targetFolderId) {
                            folderIds.push(folderId);
                        }
                    } else {
                        const fileId = this.getRowId(trEl);
                        if (fileId) {
                            fileIds.push(fileId);
                        }
                    }
                }
            });
        } else {
            // 3. If nothing is checked, fall back to the single item
            if (isFolder) {
                folderIds.push(singleFileOrFolderId);
            } else {
                fileIds.push(singleFileOrFolderId);
            }
        }

        return {
            fileIds,
            folderIds,
        };
    }

    private highlightDropTarget(target: HTMLElement, isMove = false, highlight = true): void {
        const className = 'drop-zone__highlight';

        // Handle folder row to move or upload file to
        const rowElement: HTMLTableRowElement | null = this.getTargetRow(target);
        if (rowElement) {
            if (this.isTargetAFolder(rowElement)) {
                if (highlight) {
                    this.element.nativeElement.classList.remove(className);
                    rowElement.classList.add(className);
                } else {
                    rowElement.classList.remove(className);
                }
                // handle highlighting for d'n'd a file to a folder containing at least one file
            } else if (!isMove) {
                if (highlight) {
                    this.element.nativeElement.classList.add(className);
                } else {
                    this.element.nativeElement.classList.remove(className);
                }
            }
        }

        // Handle upload area when no files are there (tr.drop-zone or div > .drop-zone)
        const dropZoneElement: HTMLElement | null = this.getDropZoneElement(target);
        if (dropZoneElement && !isMove) {
            if (highlight) {
                dropZoneElement.classList.add(className);
            } else {
                dropZoneElement.classList.remove(className);
            }
        }
    }

    private getDropTarget(target: HTMLElement): HTMLElement {
        if (this.uploaderKey === UPLOADER_KEY.MY_FILES_UPLOAD_UPLOADER_KEY) {
            const rowElement: HTMLTableRowElement | null = this.getTargetRow(target);
            if (rowElement) {
                if (this.isTargetAFolder(rowElement)) {
                    return rowElement;
                }
            }
        }
        return this.element.nativeElement;
    }

    private dragLeftTheElement(dropTarget: HTMLElement, event: DragEvent): boolean {
        // Get the location on screen of the element.
        const rect = dropTarget.getBoundingClientRect();
        // Check the dragEvent coordinates are outside of the rectangle
        if (event.x > rect.left + rect.width - 1 || event.x < rect.left || event.y > rect.top + rect.height - 1 || event.y < rect.top) {
            return true;
        }
        return false;
    }

    /**
     * Initializes the uploader, preparing it to upload files to a specific dataset.
     */
    private initUploader(): void {
        if (!this.uploaderKey) {
            return;
        }
        this.uploader = this.fetchOrInitUploader(this.uploaderKey);

        // the BlobType.UserFile uses the approach where the uploaded file has to have a file name specified,
        // e.g. the SAS token has to have file scope, not container
        // in the future, the majority of cases will have to use this approach, where all files are tracked in the database
        // the exceptions might be:
        // - profile image - there is always only one
        // - header image - there is always one
        // - any other files, which don't have to be tracked in the DB, test files, for example, need to be tracked in DB

        if (this.blobType === BlobType.UserFile || this.isUploadToDataset) {
            this.setUploaderEvents();
            return;
        }

        const blobParameter = this.getBlobParameter();

        if (!blobParameter) {
            this.userNotificationService.showError('UPLOADER.ERRORS.INIT_BLOB_PARAM_FAILED');
            return;
        }

        this.fileUploadService
            .postBlob$(blobParameter)
            .pipe(
                tap(({ sas }: UploadInfo) => {
                    if (sas != null) {
                        this.setUploaderEvents();

                        if (this.base64StringToUpload != null) {
                            this.startBase64UploadIfFileProvided();
                        }
                    }
                }),
                catchError(() => {
                    this.userNotificationService.showError('UPLOADER.ERRORS.FAILED_TO_FETCH_UPLOAD_INFO');
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    private getBlobParameter(): BlobParameter | undefined {
        let blobParameter: BlobParameter;
        if (this.blobType === BlobType.HeaderImage || this.blobType === BlobType.ProfileImage) {
            blobParameter = { blobType: this.blobType };
            return blobParameter;
        } else if (
            this.pathToAppend &&
            (this.blobType === BlobType.WorkflowImage ||
                this.blobType === BlobType.ModuleImage ||
                this.blobType === BlobType.TestFile ||
                this.blobType === BlobType.EmbeddedImages ||
                this.blobType === BlobType.UserVerificationDocument)
        ) {
            blobParameter = { blobType: this.blobType, pathToAppend: this.pathToAppend };
            return blobParameter;
        }
    }

    /**
     * If an @Input parameter 'base64StringToUpload' is provided, we convert this bas64 string
     * into a UploadFileItem and trigger the upload.
     */
    private startBase64UploadIfFileProvided(): void {
        if (this.base64StringToUpload != null) {
            fetch(this.base64StringToUpload).then(async (res: Response) => {
                const blob: Blob = await res.blob();
                const type: string = blob.type;
                const fileTypeExtension: string = type.split('/')[1];
                let fileName: string = 'pasted-image-' + new Date().getTime() + '.' + fileTypeExtension;
                if (this.pathToAppend != null) {
                    fileName = this.pathToAppend;
                }
                const file: File = new File([blob], fileName, { type });

                this.uploader.addToQueue([file]);
            });
        }
    }

    private fetchOrInitUploader(uploaderKey: string | undefined): FileUploader {
        // if found in cache, use it
        if (uploaderKey != null && this.fileUploadService.fetchFromCache(uploaderKey) != null) {
            return <FileUploader>this.fileUploadService.fetchFromCache(uploaderKey);
        }

        const uploader: FileUploader = new FileUploader({
            autoUpload: false,
            url: '',
        });

        // if uploader key is defined, but not in cache (first if statement), then add to the cache
        if (uploaderKey != null) {
            this.fileUploadService.addToUploaderCache(uploaderKey, uploader);
        }

        return uploader;
    }

    private setUploaderEvents(): void {
        this.uploader.onAfterAddingAll = (fileItems: UploadFileItem[]): void => {
            if (this.createTargetFolder && !this.targetFolderId) {
                this.userNotificationService.showSpinner();
                this.fileService
                    .createNestedFolders$(this.createTargetFolder!.subfolderNames)
                    .pipe(
                        tap((resultFolder: Folder) => {
                            this.targetFolderId = resultFolder.id;
                            this.targetFolderCreated.emit(resultFolder);
                            this.prepareFilesAndStartUpload(fileItems);
                        }),
                        catchError((err: unknown) => {
                            if (err instanceof HttpErrorResponse && err.status === 409) {
                                this.userNotificationService.showError(
                                    this.translateService.instant('UPLOADER.ERRORS.TARGET_FOLDER_ALREADY_EXISTS', {
                                        resultName: this.createTargetFolder!.subfolderNames[this.createTargetFolder!.subfolderNames.length - 1],
                                    }) as string,
                                );
                            } else {
                                this.userNotificationService.showError('UPLOADER.ERRORS.ERROR_CREATING_TARGET_FOLDER');
                            }
                            this.cancelAllUploads = { cancel: true };
                            return EMPTY;
                        }),
                        finalize(() => {
                            this.userNotificationService.hideSpinner();
                        }),
                    )
                    .subscribe();
            } else {
                this.prepareFilesAndStartUpload(fileItems);
            }
        };
    }

    private prepareFilesAndStartUpload(fileItems: UploadFileItem[]): void {
        let fileItemsUpdated = fileItems.map((fileItem: UploadFileItem) => {
            // we need to access protected "options" property on fileItem since it's carrying additional parameters
            // where we set "targetFolderId" and because of that we are casting to any
            // TODO Check if and how easy is to access options property, or if we can use some other property to set
            // targetFolderId so that we avoid casting to any.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const targetFolderId: string | undefined = (fileItem as any).options.additionalParameter?.targetFolderId;
            // if fileItem does not have targetFolderId set already when using drop use the targetFolderId from directive because
            // if user drops on folder then files should uploaded not in current folder by the folder where they were dropped and
            // this is set in onDrop event handler in uploadFiles method
            if (!targetFolderId) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (fileItem as any).options = this.getOptions(this.targetFolderId);
            }
            return fileItem;
        });

        fileItemsUpdated = this.replaceFileNameSpecialChars(fileItemsUpdated);
        fileItemsUpdated = FileUtils.findAndRenameDuplicateFilenamesWithoutDiacritics(fileItemsUpdated);

        this.onFilesAdded.emit(fileItemsUpdated);
        const filesCurrentlyUploading = this.filesValidatorService.getCurrentlyUploadingFiles(this.uploader.queue, fileItemsUpdated.length);
        this.filesValidatorService
            .getValidAndInvalidFiles$<UploadFileItem>(fileItemsUpdated, this.uploadValidationSelectedFiles, [...filesCurrentlyUploading], {
                errorNotificationMode: this.errorNotificationMode,
                isFileTypeSupportedOptions: { supportedFileTypes: this.supportedFileTypes, errorMessageKey: this.notSupportedFileTypeCustomMessage },
                isMaxFileSizeExceededOptions: {
                    maxInputSizeInBytes: this.maxInputSizeInBytes,
                    showGeneralMaxInputSizeMessage: this.showGeneralMaxInputSizeMessage,
                },
                checkFileNameOptions: {
                    allowSameFileNames: this.allowSameFileNames,
                },
            })
            .pipe(
                tap((validAndInvalidFiles: ValidAndInvalidFiles<UploadFileItem>) => {
                    validAndInvalidFiles.invalidFiles.forEach((f: UploadFileItem) => {
                        this.removeFileFromQueue(f);
                    });
                    this.triggerUpload(validAndInvalidFiles.validFiles);
                    if (validAndInvalidFiles.invalidFiles.length > 0) {
                        this.invalidFiles.emit(validAndInvalidFiles.invalidFiles);
                    }
                }),
                catchError(() => {
                    fileItemsUpdated.forEach((f: UploadFileItem) => {
                        this.removeFileFromQueue(f);
                    });
                    return EMPTY;
                }),
                take(1),
            )
            .subscribe();
    }

    // replace special chars at the beginning for the validations inside this component as well as outside of it being called by the "onFilesAdded" will work
    private replaceFileNameSpecialChars(fileItems: UploadFileItem[]): UploadFileItem[] {
        return fileItems.map((fileToUpload: UploadFileItem) => {
            fileToUpload.file.name = FileUtils.replaceDiacriticsInFileName(fileToUpload.file.name || '');
            fileToUpload.file.name = FileUtils.replaceFileNameInvalidCharacters(fileToUpload.file.name);
            return fileToUpload;
        });
    }

    private handleAllUploadsCompleted(): void {
        // We have a timeout here, because this event gets emitted when the callback comes back from the BLOB STORAGE.
        // At that time, the DB might not have been notified yet that the uploads have been finished (because the Blob storage notifies the DB).
        // So it might happen, that, newly uploaded files do not show up in i.e. My Files list (because we filter out Pending files).
        setTimeout(() => {
            this.uploader.isUploading = false;
            this.onAllUploadsCompleted.emit();
        }, 1000);
    }

    private triggerUpload(filesToUpload: UploadFileItem[]): void {
        if (filesToUpload?.length > 0 && !filesToUpload.some(file => file.unsupportedFileTypeError)) {
            this.uploader.isUploading = true;
            this.onUploadStarted.emit();

            from(filesToUpload)
                .pipe(
                    mergeMap((f: UploadFileItem) => {
                        const cancelUpload$$ = new Subject<boolean>();
                        this.cancelUploadMap.set(f, cancelUpload$$);
                        return this.uploadFile$(f).pipe(takeUntil(cancelUpload$$));
                    }, this.concurrency),
                    takeUntil(this.cancelAllUploads$$),
                )
                .subscribe();
        }
    }

    private uploadFile$(fileToUpload: UploadFileItem): Observable<BlobUploadCommonResponse | string> {
        if (this.isUploadToDataset) {
            return this.uploadFileToDataset$(fileToUpload);
        } else {
            return this.uploadUserFile$(fileToUpload, this.blobType);
        }
    }

    private uploadFileToDataset$(fileItem: UploadFileItem): Observable<BlobUploadCommonResponse> {
        return this.fileUploadService.createDbEntryForDatasetFile$(fileItem.file.name || '', this.datasetId!, this.datasetFileType!).pipe(
            switchMap((uploadInfo: DatasetFileResponse) => {
                const uploadUrl = uploadInfo.sasTokenUri + uploadInfo.sasToken;
                const readToken = uploadInfo.sasTokenUri + uploadInfo.sasReadToken;
                const fileModel = new FileModel(uploadInfo.fileId);
                fileModel.url = readToken;
                fileModel.token = readToken;
                fileModel.uploadToken = uploadUrl;
                fileModel.fullpath = uploadInfo.fullpath;
                fileModel.name = fileItem.file.name || '';
                fileModel.sizeInBytes = fileItem.file.size;
                fileModel.type = FileUtils.getFileExtension(fileModel.name);
                fileModel.createdAt = uploadInfo.createdAt ?? new Date().toString();
                fileModel.storageType = 'Azure';
                return this.uploadUserFileBlob$(fileItem, undefined, fileModel);
            }),
            catchError((err: unknown) => {
                this.handleFileUploadFailure(fileItem, err as HttpErrorResponse);
                return EMPTY;
            }),
        );
    }

    private uploadUserFile$(fileItem: UploadFileItem, blobType: BlobType): Observable<BlobUploadCommonResponse> {
        return this.generateSasUrlForUpload$(fileItem, blobType).pipe(
            switchMap((uploadInfo: UploadInfo) => {
                const readUrl = uploadInfo.uri + uploadInfo.sasRead;
                // TODO: this is where we need to create a new instance for the uploader to instantiate
                // files of instance FileModelMED - creates many type issues
                const fileModel = new FileModel(
                    uploadInfo.fileId,
                    readUrl,
                    fileItem.file.lastModifiedDate as string,
                    this.fileStorageType,
                    fileItem.file.size || 0,
                );
                fileModel.uploadToken = uploadInfo.uri + uploadInfo.sas;
                return this.uploadUserFileBlob$(fileItem, uploadInfo, fileModel);
            }),
            catchError((err: unknown) => {
                this.handleFileUploadFailure(fileItem, err as HttpErrorResponse);
                return EMPTY;
            }),
        );
    }

    private handleFileUploadFailure(fileToUpload: UploadFileItem, error: HttpErrorResponse): void {
        let errorMsg = 'File could not be uploaded.';

        // annotations upload "file matching" error
        if (error?.error?.error?.startsWith('Can not find image file with the name')) {
            errorMsg = ' No file with the same name found for this annotation.';
        } else {
            if (typeof error?.error?.errors === 'string') {
                errorMsg = errorMsg + error?.error?.errors;
            }
            if (typeof error?.error?.errors === 'object') {
                errorMsg = errorMsg + Object.values(error.error.errors).join(', ');
            }
        }

        this.userNotificationService.showError(errorMsg);

        fileToUpload.isUploading = false;
        fileToUpload.isUploaded = false;
        fileToUpload.isSuccess = false;
        // there is some inconsistency in this._uploader queue which is stored in cache
        // and fileUpload component queue which leads to file tried to be uploaded twice
        // so we need to check if file is still in the queue before removing it
        if (this.uploader.queue.find((f: UploadFileItem) => f.file.name === fileToUpload.file.name) != null) {
            this.uploader.removeFromQueue(fileToUpload);
        }

        this.onUploadCompleted.emit();
        this.checkIfAllUploadsCompleted();
    }

    private uploadUserFileBlob$(fileItem: UploadFileItem, uploadInfo: UploadInfo | undefined, fileModel: FileModel): Observable<BlobUploadCommonResponse> {
        // after the file was uploaded, the same file model is used to notify the other components
        // that the file is uploaded and those components should use the readToken, not the original token
        // which has only the write scope
        const blockBlobClient: BlockBlobClient = new BlockBlobClient(fileModel.uploadToken!, this.pipeline);

        const fileSize = fileItem.file.size;
        const progressionCallBack = (uploadProgress): void => {
            fileItem.progress = (uploadProgress.loadedBytes / fileSize) * 100;
        };

        const abortController = new AbortController();
        this.abortControllerMap.set(fileItem, { fileId: fileModel.id, abortController });
        const options: BlockBlobParallelUploadOptions = {
            onProgress: progressionCallBack,
            abortSignal: abortController.signal,
            maxSingleShotSize: 4,
        };

        return from(blockBlobClient.uploadData(fileItem['some'], options)).pipe(
            map((item: BlobUploadCommonResponse) => {
                if (uploadInfo?.fileId != null) {
                    this.onFileCreated.emit(fileModel);
                }

                this.handleFileUploadCallback(fileItem, fileModel);

                return item;
            }),
            catchError((e: unknown) => {
                const errorMessage = 'file: ' + fileItem.file.name + ' failed to be uploaded. Please try again.';
                console.error(errorMessage, e);
                this.userNotificationService.showError(errorMessage);

                return EMPTY;
            }),
        );
    }

    private generateSasUrlForUpload$(fileItem: UploadFileItem, blobType: BlobType): Observable<UploadInfo> {
        // 411083: Prevent images from overriding by correctly prepending this.pathToAppend
        let pathToAppend = fileItem.file.name || '';
        if (this.pathToAppend != null) {
            pathToAppend = this.pathToAppend + '/' + fileItem.file.name;
        }

        const uploadRequest: BlobParameter = {
            blobType,
            pathToAppend,
        };
        // we need to access protected "options" property on fileItem since it's carrying additional parameters
        // where we set "targetFolderId" and because of that we are casting to any
        // TODO Check if and how easy is to access options property, or if we can use some other property to set
        // targetFolderId so that we avoid casting to any.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let targetFolderId: string | undefined = (fileItem as any).options.additionalParameter?.targetFolderId;
        if (!targetFolderId && this.uploader.options.additionalParameter?.targetFolderId) {
            targetFolderId = this.uploader.options.additionalParameter.targetFolderId;
        }
        if (targetFolderId) {
            uploadRequest.targetFolderId = targetFolderId;
        }
        return this.fileUploadService.postBlob$(uploadRequest);
    }

    private createPipeline(): void {
        const sasCredential = new AnonymousCredential();
        this.pipeline = newPipeline(sasCredential, <StoragePipelineOptions>{ keepAliveOptions: { enable: false } });
    }

    private handleFileUploadCallback(fileToUpload: UploadFileItem, fileModel: FileModel): void {
        fileToUpload.id = fileModel.id;
        fileToUpload.url = fileModel.token;
        fileToUpload.isUploading = false;
        fileToUpload.isUploaded = true;
        fileToUpload.isSuccess = true;
        // there is some inconsistency in this._uploader queue which is stored in cache
        // and fileUpload component queue which leads to file tried to be uploaded twice
        // so we need to check if file is still in the queue before removing it
        if (this.uploader.queue.find((f: UploadFileItem) => f.url === fileToUpload.url) != null) {
            this.uploader.removeFromQueue(fileToUpload);
        }

        if (fileToUpload.unsupportedFileTypeError || fileToUpload.sameNameExistsError || fileToUpload.sameNameExistsMoreThanOnceError) {
            this.onUploadCompleted.emit();
        } else {
            this.onUploadCompleted.emit(fileModel);
        }
        this.checkIfAllUploadsCompleted();
    }

    private checkIfAllUploadsCompleted(): void {
        if (this.uploader.queue.length === 0) {
            this.handleAllUploadsCompleted();
        }
    }

    private removeFileFromQueue(file: UploadFileItem): void {
        this.uploader.removeFromQueue(file);

        if (this.uploader.queue.length === 0) {
            this.uploader.isUploading = false;
        }
    }
}
